/**
 * THE LAYERS - what the video studio lays over a cover, ported from RaceCut's
 * lib/racecut/studio-layers.ts (the caption style, the phrase blocks, the
 * singer-photo entrances). Everything is placed in fractions of the frame, so
 * the small preview canvas and the 1080p export draw it in the same spot.
 */
import {
  CAPTION_SIZE,
  presetNamed,
  type CaptionPresetId,
} from "./captionPresets";
export { CAPTION_SIZE };

/** One sung word, in song seconds. `phraseStart`/`phraseEnd` pin a block typed by hand. */
export type CaptionWord = {
  text: string;
  start: number;
  end: number;
  phraseStart?: boolean;
  phraseEnd?: boolean;
};

export type Entrance = "pop" | "slide" | "cut";
export type Dance = "still" | "float" | "sway" | "breathe";
export type CaptionFont =
  | "arial-black"
  | "impact"
  | "arial"
  | "georgia"
  | "trebuchet"
  | "courier";
export type TextCase = "as-is" | "upper" | "lower" | "title";
export type Align = "left" | "center" | "right";
/** `x` is the word's anchor (its centre when aligned centre), `y` its foot. */
/** The preset, and its knobs as the person left them: font, size, case, the text colour, and the accent/glow/pill colours the preset brought. */
export type CaptionStyle = {
  preset: CaptionPresetId;
  size: number;
  x: number;
  y: number;
  font: CaptionFont;
  color: string;
  accent: string;
  glow: string;
  pill: string;
  bold: boolean;
  italic: boolean;
  textCase: TextCase;
  align: Align;
};

/** A style that starts as the preset turned it, keeping where the lyrics sit. */
export function styledAs(
  id: CaptionPresetId,
  keep: Pick<CaptionStyle, "x" | "y" | "align">,
): CaptionStyle {
  return { preset: id, ...presetNamed(id).knobs, ...keep };
}

/** Families every device has, so the canvas and the export draw the same glyphs. */
export const CAPTION_FONTS: Record<CaptionFont, string> = {
  "arial-black": '"Arial Black", "Arial Bold", Arial, sans-serif',
  impact: 'Impact, "Arial Narrow Bold", sans-serif',
  arial: "Arial, Helvetica, sans-serif",
  georgia: 'Georgia, "Times New Roman", serif',
  trebuchet: '"Trebuchet MS", Verdana, sans-serif',
  courier: '"Courier New", Courier, monospace',
};

export function captionFontCss(px: number, style: CaptionStyle): string {
  return `${style.italic ? "italic " : ""}${style.bold ? "900" : "400"} ${px}px ${CAPTION_FONTS[style.font]}`;
}

export function captionText(text: string, textCase: TextCase): string {
  if (textCase === "upper") return text.toUpperCase();
  if (textCase === "lower") return text.toLowerCase();
  if (textCase === "title")
    return text.replace(
      /\p{L}[\p{L}\p{M}]*/gu,
      (word) => word[0].toUpperCase() + word.slice(1).toLowerCase(),
    );
  return text;
}

/** Canvas draws Arial Black ~34% heavier than libass, so production's H*0.08 became H*0.06 (RaceCut song-card captions). */
const CAPTION_HEIGHT = 0.06;
export function captionFontPx(
  frameHeight: number,
  style: CaptionStyle,
): number {
  return Math.round(frameHeight * CAPTION_HEIGHT * style.size);
}

/** `shift` in fractions of the frame's width, `turn` in radians. */
export type PortraitMoment = {
  shift: number;
  scale: number;
  turn: number;
  alpha: number;
};

type Key = {
  at: number;
  shift: number;
  scale: number;
  turn: number;
  alpha: number;
};

/** The genre's entrances as splines: slide in, overshoot the scale, a little turn - it enters from its own side. */
const ENTRANCES: Record<Entrance, { secs: number; keys: Key[] }> = {
  pop: {
    secs: 0.7,
    keys: [
      { at: 0, shift: 0.205, scale: 0.4, turn: 10, alpha: 0 },
      { at: 0.55, shift: -0.018, scale: 1.16, turn: -4, alpha: 1 },
      { at: 0.8, shift: 0.006, scale: 0.97, turn: 1.5, alpha: 1 },
      { at: 1, shift: 0, scale: 1, turn: 0, alpha: 1 },
    ],
  },
  slide: {
    secs: 0.5,
    keys: [
      { at: 0, shift: 0.38, scale: 1, turn: 3, alpha: 0 },
      { at: 0.7, shift: -0.023, scale: 1, turn: -1, alpha: 1 },
      { at: 1, shift: 0, scale: 1, turn: 0, alpha: 1 },
    ],
  },
  cut: { secs: 0, keys: [{ at: 0, shift: 0, scale: 1, turn: 0, alpha: 1 }] },
};

export function portraitMoment(
  entrance: Entrance,
  sinceSecs: number,
  fromLeft: boolean,
): PortraitMoment {
  const { secs, keys } = ENTRANCES[entrance];
  const progress =
    secs > 0 ? easeOut(Math.max(0, Math.min(1, sinceSecs / secs))) : 1;
  const next = keys.findIndex((key) => key.at >= progress);
  const to = keys[next < 0 ? keys.length - 1 : next];
  const from = keys[Math.max(0, (next < 0 ? keys.length - 1 : next) - 1)];
  const along =
    to.at === from.at ? 1 : (progress - from.at) / (to.at - from.at);
  const between = (a: number, b: number) => a + (b - a) * along;
  const side = fromLeft ? -1 : 1;
  return {
    shift: between(from.shift, to.shift) * side,
    scale: between(from.scale, to.scale),
    turn: (between(from.turn, to.turn) * side * Math.PI) / 180,
    alpha: between(from.alpha, to.alpha),
  };
}

/** `rise` in fractions of the photo's height, `turn` in radians. */
export type DanceMoment = { rise: number; scale: number; turn: number };

export function danceMoment(dance: Dance, sinceSecs: number): DanceMoment {
  const wave = (periodSecs: number) =>
    Math.sin((sinceSecs * 2 * Math.PI) / periodSecs);
  if (dance === "float") return { rise: wave(2.6) * 0.04, scale: 1, turn: 0 };
  if (dance === "sway")
    return { rise: 0, scale: 1, turn: (wave(2.2) * 4 * Math.PI) / 180 };
  if (dance === "breathe")
    return { rise: 0, scale: 1 + wave(2) * 0.03, turn: 0 };
  return { rise: 0, scale: 1, turn: 0 };
}

/** cubic-bezier(.22, 1, .36, 1): fast in, soft landing. */
function easeOut(t: number): number {
  const curve = (s: number, p1: number, p2: number) =>
    3 * (1 - s) * (1 - s) * s * p1 + 3 * (1 - s) * s * s * p2 + s * s * s;
  const slope = (s: number, p1: number, p2: number) =>
    3 * (1 - s) * (1 - s) * p1 +
    6 * (1 - s) * s * (p2 - p1) +
    3 * s * s * (1 - p2);
  let s = t;
  for (let i = 0; i < 8; i++) {
    const off = curve(s, 0.22, 0.36) - t;
    const d = slope(s, 0.22, 0.36);
    if (Math.abs(off) < 1e-5 || !d) break;
    s -= off / d;
  }
  return curve(s, 1, 1);
}

/** A run of words with no real pause between them - the block CapCut hands you to edit. */
export type Phrase = {
  first: number;
  last: number;
  start: number;
  end: number;
  text: string;
};

/** Whisper pauses this long between phrases; shorter gaps are just the singer breathing. */
const PHRASE_GAP_SECS = 0.45;
/** Two lines at the smallest caption size; CapCut's blocks run 80-100 and it hands you the scissors. */
const PHRASE_MAX_CHARS = 26;
const CUT_AT_PUNCTUATION = 0.5;
const CUT_AT_A_PAUSE = 0.5;

/** The blocks as Whisper heard them, cut down to size where it heard a whole verse in one breath. Blocks typed by hand stay as typed. */
export function phrasesOf(words: CaptionWord[]): Phrase[] {
  return phrasesByPauses(words).flatMap((phrase) =>
    words[phrase.first].phraseStart ? [phrase] : cutToFit(words, phrase),
  );
}

function phrasesByPauses(words: CaptionWord[]): Phrase[] {
  const phrases: Phrase[] = [];
  words.forEach((word, i) => {
    const open = phrases[phrases.length - 1];
    const continues =
      open &&
      !words[open.last].phraseEnd &&
      !word.phraseStart &&
      word.start - words[open.last].end < PHRASE_GAP_SECS;
    if (continues) {
      open.last = i;
      open.end = word.end;
      open.text += " " + word.text;
    } else {
      phrases.push(phraseOf(words, i, i));
    }
  });
  return phrases;
}

function cutToFit(words: CaptionWord[], phrase: Phrase): Phrase[] {
  if (phrase.text.length <= PHRASE_MAX_CHARS || phrase.last === phrase.first)
    return [phrase];
  const cut = bestCut(words, phrase);
  return [
    ...cutToFit(words, phraseOf(words, phrase.first, cut)),
    ...cutToFit(words, phraseOf(words, cut + 1, phrase.last)),
  ];
}

/** The last word of the first half: after a comma or a breath when there is one, near the middle otherwise. */
function bestCut(words: CaptionWord[], phrase: Phrase): number {
  const scored = Array.from({ length: phrase.last - phrase.first }, (_, k) => {
    const i = phrase.first + k;
    const left = textOf(words, phrase.first, i).length,
      right = textOf(words, i + 1, phrase.last).length;
    const balance = -Math.abs(left - right) / (left + right);
    const punctuation = /[,.;:!?…]$/.test(words[i].text)
      ? CUT_AT_PUNCTUATION
      : 0;
    const pause =
      Math.min(1, (words[i + 1].start - words[i].end) / PHRASE_GAP_SECS) *
      CUT_AT_A_PAUSE;
    return { i, score: balance + punctuation + pause };
  });
  return scored.reduce((best, cut) => (cut.score > best.score ? cut : best)).i;
}

function phraseOf(words: CaptionWord[], first: number, last: number): Phrase {
  return {
    first,
    last,
    start: words[first].start,
    end: words[last].end,
    text: textOf(words, first, last),
  };
}

function textOf(words: CaptionWord[], first: number, last: number): string {
  return words
    .slice(first, last + 1)
    .map((word) => word.text)
    .join(" ");
}
